import { useState, useEffect } from 'react';
import { getProject, getAudits, getSitemap, getPositioningAnalyses, extractTechnicalInfo } from '../api';
import { useToast } from '../ToastContext';

export default function useProjectSynthese(slug, projectFromContext = null) {
    const { showToast } = useToast();
    const [syntheseData, setSyntheseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [extractingTech, setExtractingTech] = useState(false);

    async function fetchSyntheseData() {
        if (!slug) return;
        try {
            setLoading(true);

            const projectPromise = projectFromContext ? Promise.resolve(projectFromContext) : getProject(slug).catch(() => ({}));
            const [projectData, audits, sitemapResult, positioningAnalyses] = await Promise.all([
                projectPromise,
                getAudits(slug).catch(() => []),
                getSitemap(slug).catch(() => ({ urls: [] })),
                getPositioningAnalyses(slug).catch(() => [])
            ]);

            const pagesSitemap = sitemapResult.urls?.length || 0;
            const validUrls = (sitemapResult.urls || []).filter(url => typeof url === 'string' && url.trim().length > 0 && url.startsWith('http'));
            const hasSitemap = validUrls.length > 0;

            const normUrl = (u) => (u || '').trim().replace(/\/+$/, '');
            const validUrlSet = new Set(validUrls.map(normUrl));

            // Si sitemap dispo: on aligne sur ses URLs. Sinon: on prend les audits existants.
            const sitemapAudits = hasSitemap
                ? audits.filter(a => validUrlSet.has(normUrl(a.url)))
                : audits;

            // Lire les scores directement depuis les champs persistés en base sur le projet
            // Ces scores sont calculés et sauvegardés par le backend lors de chaque audit
            const scoreSeoMoyen = projectData.score_seo || 0;
            const scorePerformanceMoyen = projectData.score_performance || 0;
            const scoreTechniqueMoyen = projectData.score_technique || 0;
            const scoreGlobalMoyen = projectData.score_global || 0;

            // Compteurs pour l'affichage (calculés depuis les audits)
            const validAudits = sitemapAudits.filter(a => a.score_global !== null && a.score_global !== undefined);
            const refUrlSet = new Set(
                (projectData.reference_pages || [])
                    .map((u) => normUrl(u))
                    .filter(Boolean),
            );
            const validPerformanceAudits = sitemapAudits.filter(
                (a) =>
                    a.pagespeed_average != null &&
                    a.pagespeed_average > 0 &&
                    refUrlSet.has(normUrl(a.url)),
            );

            const positioningData = positioningAnalyses?.[0] || projectData.positioning_analyses?.[0] || {};

            const secteur = positioningData.secteur || projectData.secteur || '';
            const comprehension = positioningData.comprehension || projectData.comprehension_ia || '';
            const pointsForts = positioningData.points_forts || projectData.points_forts || [];
            const pointsFaibles = positioningData.points_faibles || projectData.points_faibles || [];
            const todoList = positioningData.todo_list || projectData.todolist || [];
            const rawGeo = positioningData.raw_json?.geo_score || {};

            // Site multilingue : priorité aux données projet (extraction "Actualiser"), sinon dérivé des audits
            let isMultilingual = projectData.is_multilingual === true;
            let hreflangLanguages = (projectData.hreflang_languages || '').trim();
            if (!hreflangLanguages && isMultilingual === false) {
                for (const a of sitemapAudits) {
                    const am = a.advanced_metrics || {};
                    if (am.is_multilingual === true) {
                        isMultilingual = true;
                        const alts = am.hreflang_alternates || [];
                        const langs = [...new Set(alts.map(x => x.lang).filter(l => l && l !== 'x-default'))].sort();
                        if (langs.length) hreflangLanguages = langs.join(', ');
                        break;
                    }
                }
                if (!hreflangLanguages && isMultilingual) {
                    const firstWithAlts = sitemapAudits.find(a => (a.advanced_metrics?.hreflang_alternates?.length || 0) > 0);
                    if (firstWithAlts?.advanced_metrics?.hreflang_alternates) {
                        const langs = [...new Set(firstWithAlts.advanced_metrics.hreflang_alternates.map(x => x.lang).filter(l => l && l !== 'x-default'))].sort();
                        hreflangLanguages = langs.join(', ');
                    }
                }
            }

            const synthese = {
                key_metrics: {
                    score_seo_moyen: Math.round(scoreSeoMoyen),
                    score_performance_moyen: Math.round(scorePerformanceMoyen),
                    score_technique_moyen: Math.round(scoreTechniqueMoyen),
                    score_global_moyen: Math.round(scoreGlobalMoyen * 10) / 10,
                    pages_sitemap: pagesSitemap,
                    sitemap_missing: !hasSitemap,
                    score_penalty_points: projectData.score_penalty_points || 0,
                    sitemap_notice: projectData.sitemap_notice || null,
                    url_discovery_source: projectData.url_discovery_source || null,
                    nb_audits_seo: validAudits.length,
                    nb_audits_performance: validPerformanceAudits.length,
                    position_moyenne: 0
                },
                site_recap: {
                    secteur: secteur,
                    comprehension_ia: comprehension,
                    description: projectData.description || ''
                },
                technical_info: {
                    hosting: projectData.hosting || '',
                    technology: projectData.technology || '',
                    cms: projectData.cms || '',
                    marketing_tools: projectData.marketing_tools || '',
                    cdn: projectData.cdn || '',
                    server: projectData.server || '',
                    framework: projectData.framework || '',
                    is_multilingual: isMultilingual,
                    hreflang_languages: hreflangLanguages,
                    has_llm_file: projectData.has_llm_file ?? null,
                },
                analysis: {
                    points_forts: Array.isArray(pointsForts) ? pointsForts : [],
                    points_faibles: Array.isArray(pointsFaibles) ? pointsFaibles : [],
                },
                positioning: {
                    todo_list: Array.isArray(todoList) ? todoList : [],
                    score_global_geo: rawGeo.score_global_geo ?? positioningData.score_positionnement ?? null,
                    alertes_prioritaires: rawGeo.alertes_prioritaires || [],
                    raw_json: positioningData.raw_json || null,
                },
            };

            setSyntheseData(synthese);
            setError(null);
        } catch (err) {
            setError('Erreur lors du chargement des données de synthèse');
            console.error('Erreur fetch synthese:', err);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (slug) fetchSyntheseData();
    }, [slug, projectFromContext?.date_dernier_audit, projectFromContext?.score_global]);

    const extractTechnicalInfoAction = async (projectUrl) => {
        if (!projectUrl || !slug) return;
        try {
            setExtractingTech(true);
            let url = projectUrl.trim();
            if (!url.startsWith('http')) url = `https://${url}`;

            await extractTechnicalInfo(slug, url);
            // Reload
            await fetchSyntheseData();
            showToast('Infos techniques mises à jour', 'success');
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || 'Erreur';
            showToast(`Erreur extraction: ${msg}`, 'error');
        } finally {
            setExtractingTech(false);
        }
    };

    return {
        syntheseData,
        loading,
        error,
        extractingTech,
        extractTechnicalInfoAction,
        reload: fetchSyntheseData
    };
}
